import { ChangeDetectorRef, Component, EventEmitter, OnChanges, OnInit, Input, Output, SimpleChanges } from '@angular/core';
import { ACHInfo } from '../shared/billing/dto/ach-info.dto';
import { SelectItem } from 'primeng/api';
import { BankAccountTypesEnum } from '../shared/billing/dto/bank-account-types.enum';
import { ArbCreateDto } from '../shared/billing/dto/arb-create.dto';
import { ArbContactInfoDto } from '../shared/billing/dto/arb-contact-info.dto';
import { ProfileMin } from 'src/app/profiles/shared/profile-min.model';
import { ProfileAddress } from 'src/app/profiles/shared/profileAddress.model';
import { PrecznService } from '../../integrations/preczn/shared/preczn.service';
import { TokenizeGatewayDto } from '../shared/gateway-resolution/dto/tokenize-gateway.dto';
import { NotificationService } from '../../shared/notification/notification.service';
import { TranslationService } from '../../shared/translation/translation.service';

// Instance counter behind precznFormId. getAchToken(formId) resolves the form by DOM id, and both
// billing hosts can have several editors on screen at once - pledge-edit-billing-list renders one
// <wl-pledge-edit-billing> per billing in an *ngFor, and profile-edit-billing-list does the same in
// its inactive view. A constant id would give two ACH widgets the same id and tokenize whichever
// the DOM happened to return first, i.e. silently the wrong bank account.
let nextPrecznAchFormId = 0;

@Component({
  selector: 'wl-ach-input',
  templateUrl: './ach-input.component.html',
  styleUrls: ['./ach-input.component.less'],
  standalone: false
})
export class AchInputComponent implements OnInit, OnChanges {
  @Input() profileNames : ProfileMin;
  @Input() defaultBillingAddress: ProfileAddress;
  /**
   * The Preczn gateway this widget must tokenize against, supplied whole or as null by the host
   * (never a partial pair). Null IS the answer "the selected payment type's gateway is not a Preczn
   * gateway" - PrecznService.resolveTokenizeGateway() only matches Preczn rows - and the widget then
   * keeps the pre-existing raw-field form, which is the correct form for an AuthNet/CardConnect
   * tenant. Unlike the Portal widget, Preczn mode here is decided by this input alone and never by
   * isPrecznOnly(): this is a direct-gateway surface whose selected payment type already names the
   * paying CCGatewayID.
   */
  @Input() resolvedGateway: TokenizeGatewayDto | null = null;
  @Output() achInfoChanges = new EventEmitter<ArbCreateDto>();
  public achInfo: ArbCreateDto = { ContactInfo: { State: ""} as ArbContactInfoDto } as ArbCreateDto;
  public bankAccountTypes: SelectItem[] = [];
  public usePreczn: boolean = false;
  public precznConfigError: boolean = false;
  public precznFormId: string = `achPaymentForm-${++nextPrecznAchFormId}`;

  constructor(
    private _precznService: PrecznService,
    private _cdr: ChangeDetectorRef,
    private _notificationService: NotificationService,
    private _translationService: TranslationService
  ) {
    Object.values(BankAccountTypesEnum).forEach(accountType => {
      this.bankAccountTypes.push({label: accountType, value: accountType})
    })
  }

  ngOnInit(): void {
    // Inputs are set before ngOnInit, so a host that already knows its gateway at mount time loads
    // the SDK from here; one that resolves it afterwards arrives through ngOnChanges below. Both
    // funnel into applyResolvedGateway(), so there is no mount ordering that loads the SDK on one
    // path and not the other.
    if (this.resolvedGateway) {
      this.applyResolvedGateway(this.resolvedGateway);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.profileNames && this.profileNames) &&
        (changes.defaultBillingAddress && this.defaultBillingAddress)) {
      this.autoPopulateProfileData(this.profileNames, this.defaultBillingAddress);
    }

    const gatewayChange = changes['resolvedGateway'];
    if (gatewayChange && !gatewayChange.firstChange) {
      this.applyResolvedGateway(gatewayChange.currentValue as TokenizeGatewayDto | null);
    }
  }

  /**
   * Retargets the widget at the gateway the host has named.
   *
   * Deferred onto a microtask rather than run inline: both call sites are lifecycle hooks running
   * inside change detection, and the detectChanges() below would otherwise re-enter it.
   */
  private applyResolvedGateway(gateway: TokenizeGatewayDto | null): void {
    void Promise.resolve().then(async () => {
      // Cleared FIRST, on every path: any token already held was minted against the previous
      // gateway, and a single-use token posted against a different merchant is worse than none.
      this.usePreczn = false;
      this.precznConfigError = false;
      this.achInfo.GatewayTokens = [];
      this.safeDetectChanges();

      if (!gateway?.precznMerchantId) {
        // Not a Preczn gateway: the raw-field form stays exactly as it was and no SDK request is
        // issued at all. This is the whole non-Preczn Backoffice tenant.
        return;
      }

      // Flag flipped and the view rendered BEFORE the load, so the data-preczn fields already exist
      // in the DOM when the SDK script runs. This mirrors the Portal widget's initial-load path,
      // which puts a detectChanges() between `usePreczn = true` and loadScript() for the same
      // reason, and it is the only ordering that is safe whether the SDK binds by DOM scan at load
      // or lazily at getAchToken().
      this.usePreczn = true;
      this.safeDetectChanges();

      try {
        // reloadForGateway() is a no-op when the SDK already serves this gateway, so mounting this
        // widget after a card widget loaded the same merchant does not tear that load down.
        await this._precznService.reloadForGateway(gateway);
      } catch {
        this.handlePrecznLoadError();
      }
      this.safeDetectChanges();
    });
  }

  // Mirrors the Portal ACH widget's reload-failure contract (and CreditCardInputComponent's): stay
  // in Preczn mode so the raw-field form is NOT offered for a gateway that would reject it, and let
  // prepareForSubmit()'s own precznConfigError rejection be what blocks the save. usePreczn is
  // already true by the time this runs; it is re-asserted so the contract survives a future caller
  // that reaches here from some other state.
  private handlePrecznLoadError(): void {
    this.usePreczn = true;
    this.precznConfigError = true;
    this._notificationService.translateError('revenue.credit-card-input.preczn-config-error');
  }

  private safeDetectChanges(): void {
    try {
      this._cdr.detectChanges();
    } catch {
      // The view can be destroyed between the microtask being queued and it running - this widget
      // sits behind *ngIf at both of its billing hosts. Nothing to update in that case.
    }
  }

  private autoPopulateProfileData(profileNames: ProfileMin, defaultBillingAddress: ProfileAddress): void {
    if (!profileNames || !defaultBillingAddress) return;

    this.achInfo.ContactInfo.FirstName = profileNames.FirstName;
    this.achInfo.ContactInfo.LastName = profileNames.LastName;
    this.achInfo.ContactInfo.OrgName = profileNames.OrgName;
    this.achInfo.ContactInfo.Address1 = defaultBillingAddress.Address1;
    this.achInfo.ContactInfo.Address2 = defaultBillingAddress.Address2;
    this.achInfo.ContactInfo.City = defaultBillingAddress.City;
    this.achInfo.ContactInfo.State = defaultBillingAddress.State;
    this.achInfo.ContactInfo.Zip = defaultBillingAddress.Zip;
    this.achInfo.ContactInfo.Country = defaultBillingAddress.Country;
  }

  emitChanges() {
    this.achInfoChanges.emit(this.achInfo)
  }

  /**
   * Mints the single-use Preczn bank-account token the ARB subscribe needs and puts it on the dto.
   *
   * Hosts must await this BEFORE their own setupArbDto(): emitChanges() below reaches the host's
   * updateACHInfo(), which assigns THIS dto to arbCreate wholesale - so a host that stamps
   * BillingId/ProfileID/PaymentTypeID first would have them overwritten, and one that never emits
   * would post an ArbCreateDto with no GatewayTokens at all.
   */
  public async prepareForSubmit(): Promise<void> {
    if (this.precznConfigError) {
      throw new Error(this._translationService.getInstant('revenue.credit-card-input.preczn-config-error'));
    }
    if (!this.usePreczn) {
      return;
    }
    // ACH tokenization, not a $0 authorization - this mints a single-use bank-account token and
    // authorizes nothing. No amount is sent.
    const token = await this._precznService.getAchToken(this.precznFormId);
    // Same tagging contract as the card widget: the gateway recorded at load time, omitted rather
    // than sent as 0 when nothing was recorded.
    const ccGatewayId = this._precznService.currentCcGatewayId;
    this.achInfo.GatewayTokens = [{ token, isMultiUse: false, ...(ccGatewayId != null ? { ccGatewayId } : {}) }];
    this.emitChanges();
  }
}
